import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AssetFaultDisplay } from 'src/app/models/assetFaultDisplay';
import { ListRowDefinition } from 'src/app/models/list-row-definition';
import { AssetFaultService } from 'src/app/services/asset-fault.service';
import { OrderableListViewModule } from 'src/app/shared/orderable-list-view/orderable-list-view.module';
import { ApplicationPipesModule } from "../../shared/module/applicationPipesModule";
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, delay, map, tap } from 'rxjs';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-asset-fault-detail',
    imports: [
        TranslateModule, CommonModule, OrderableListViewModule,
        ApplicationPipesModule, MatButtonModule, MatDatepickerModule,
        MatFormFieldModule, ReactiveFormsModule, MatGridListModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './asset-fault-detail.component.html',
    styleUrl: './asset-fault-detail.component.css'
})
export class AssetFaultDetailComponent implements OnInit, AfterViewInit {
  @Input() assetFault: AssetFaultDisplay;
  @Input() vin: string;
  @Output() close = new EventEmitter();

  loading = true;
  faultDetailForm: FormGroup<FaultForm>;
  maxDate = new Date();
  defaultStart = new Date(this.maxDate.getTime() - (1 * 24 * 60 * 60 * 1000))

  @ViewChild('dateDisplay') dateDisplay: TemplateRef<any>;

  faults: FaultDetailDisplay[] = []

  faultRowDefinition: ListRowDefinition[];

  constructor(
    private assetFaultService: AssetFaultService
  ) {
    this.faultDetailForm = new FormGroup<FaultForm>({
      dateFrom: new FormControl(this.defaultStart, { nonNullable: true }),
      dateTo: new FormControl(this.maxDate, { nonNullable: true })
    });
  }

  ngOnInit(): void {
    this.faultDetailForm.valueChanges.pipe(delay(1000), tap(_ => this.loading = true)).subscribe(() =>
       this.getData());
    this.getData();
  }

  getData(): void {
    const start = this.faultDetailForm.controls.dateFrom.value;
    let end = this.faultDetailForm.controls.dateTo.value;

    if (start && end) {
      if (end.getDate() === this.maxDate.getDate() && end.getMonth() === this.maxDate.getMonth()) {
        end = this.maxDate;
      }
      combineLatest([
        this.assetFaultService.getFaultDetail(
        this.vin,
        start,
        end,
        this.assetFault),
        this.assetFaultService.getFaultStates()
      ])
        .pipe(
          map(([queryFaults, states]) => queryFaults.map(fault =>
          ({
            occurrenceCount: fault.occurrenceCount,
            latestDateTimeUtc: fault.latestDateTimeUtc,
            faultState: states[fault.faultState]
          } as FaultDetailDisplay))),
          tap(_ => this.loading = false))
        .subscribe(queryFaults => this.faults = queryFaults);
    } else {
      this.loading = false;
    }
  }

  ngAfterViewInit(): void {
    this.faultRowDefinition = [
      {
        propertyName: 'latestDateTimeUtc',
        header: '@lastLogged',
        widthPriority: 4,
        template: this.dateDisplay
      },
      {
        propertyName: 'faultState',
        header: '@state',
        widthPriority: 1
      },
      {
        propertyName: 'occurrenceCount',
        header: '@occurrenceCount',
        widthPriority: 1
      }
    ];
  }

  onCloseClicked(): void {
    this.close.emit();
  }

}

interface FaultForm {
  dateFrom: FormControl<Date>;
  dateTo: FormControl<Date>;
}

interface FaultDetailDisplay {
  latestDateTimeUtc: Date;
  faultState: string;
  occurrenceCount: number;
}